/**
 * Optimistic Suspense fallbacks rendered from a list/rec tile seed.
 *
 * When the user clicks a product tile, the clicked `HecProduct` rides along in
 * nav state (`tileProduct`, see product-details.location-state.ts). The PDP's
 * data still streams (the `.data` request defers the SCAPI product), so the
 * Await fallbacks would otherwise show bare skeletons on every list→PDP nav.
 * These components render the "important half" we already hold from the tile —
 * hero image, title, brand, SKU, ratings, price, promo callouts, compare-to,
 * variant options, and eligibility/free-shipping badges — so the above-the-fold
 * content paints immediately and is replaced in place when the system-of-record
 * `_product` resolves. The info column mirrors the real column section-for-
 * section (see OptimisticInfoColumn) so the swap doesn't shift everything below.
 *
 * Seed-only caveats:
 * - CIO/search is NOT the system of record for product detail. Price and
 *   eligibility here are provisional; the resolved SCAPI product is authoritative
 *   and overwrites them. Markup mirrors the real column to keep the swap CLS-free.
 * - The hero reuses the tile's `type="plp"` image URL (already in the browser
 *   cache from the grid) for an instant paint; `ProductImages` upgrades it to the
 *   `type="pdp"` candidate once the product resolves.
 * - Fields the tile genuinely can't carry (short/long description, cross-sell
 *   recs, qty + ATC) keep height-reserving placeholders sized to the real markup.
 */
import { ProductImage } from "~/components/product-list/product-image";
import { Button } from "~/components/ui/button";
import { DividerLine } from "~/components/ui/divider-line";
import { Label } from "~/components/ui/label";
import { Skeleton } from "~/components/ui/skeleton";
import { Ratings } from "~/hec/components/product/ratings";
import type { HecProduct } from "~/hec/types/product";
import { getScheduledBadgeTexts } from "~/hec/types/product";
import { bySiteId } from "~/hec/utils/site-id";
import type { SerializeFrom } from "~/lib/types";
import { cn } from "~/lib/ui";
import { useFormatPrice } from "~/shop/utils/price";

import { renderBrandRow } from "../utils/render-brand-row";
import { CompareToMessage } from "./compare-to-message";
import { CrossSellSkeleton } from "./cross-sell-section";
import {
  ELIGIBILITY_SITE_LABEL_BY_SITE,
  SHOWS_FREE_SHIPPING_BADGE,
} from "./pdp-shared-constants";
import { EligibilityBadge, FreeShippingBadge } from "./product-badges";
import { PromotionCallouts } from "./promotion-callouts";

type Seed = SerializeFrom<HecProduct>;

const PDP_IMAGE_HEIGHT = "h-[370px] md:h-[600px]";

/**
 * The seed fields `renderBrandRow` reads, in one place for both seed columns
 * below — they rendered identical field-by-field literals before, which left
 * the telehealth tier's plumbing described by two comments free to disagree.
 *
 * Built explicitly rather than passing the whole seed because `c_brandName` /
 * `c_brandLeadingCopy` aren't declared `HecProduct` fields — they arrive
 * through its `[key: string]: unknown` index signature.
 *
 * `c_ownerClass` carries renderBrandRow's telehealth tier through the
 * optimistic paint. Without it the seed renders "Shop more Mounjaro" until the
 * SCAPI product resolves, then swaps to "Shop more Patiently" — a visible flash
 * on the PLP→PDP soft nav. ET-369.
 *
 * Load-bearing in `OptimisticTitleBrand` (the mobile hoisted slot is ungated,
 * so a telehealth seed reaches it); inert in `OptimisticInfoColumn`, which
 * `usesOptimisticInfoColumn` (product-details.tsx) gates to non-LMN,
 * non-telehealth seeds — telehealth takes `TelehealthInfoColumnSkeleton`, which
 * has no brand row at all.
 *
 * Best-effort, not a guarantee, on both paths: `c_ownerClass` is absent from
 * the CIO tile payload and grafted on by the PLP's SCAPI enrichment pass, which
 * skips products individually on error. Where that enrichment missed, the seed
 * looks standard and the row degrades to the pre-ET-369 behaviour for one paint
 * — the same fail-open as `usesOptimisticInfoColumn` itself, and the resolved
 * SCAPI product still corrects it.
 */
function buildBrandRowInput(seed: Seed) {
  return {
    brand: seed.brand,
    c_brandName: seed.c_brandName as string | undefined,
    c_brandURL: seed.c_brandURL,
    c_brandLeadingCopy: seed.c_brandLeadingCopy as string | undefined,
    c_ownerClass: seed.c_ownerClass,
  };
}

/**
 * Title + "Shop more {brand}", seed-backed. The resolved column hoists the same
 * block above the image on mobile, so rendering it here keeps the tile→PDP swap
 * CLS-free in the hoisted slot.
 */
export function OptimisticTitleBrand({ seed }: { seed: Seed }) {
  const name = seed.name || seed.productName || "";
  const brandLink = renderBrandRow(buildBrandRowInput(seed));
  return (
    <>
      <h1 className="font-grotesque text-xl font-medium tracking-tight text-[#142745] md:text-[1.75rem]">
        {name}
      </h1>
      {brandLink}
    </>
  );
}

/** Height-reserving skeleton for the hoisted mobile title + brand (no seed). */
export function MobileTitleBrandSkeleton() {
  return (
    <>
      <div className="space-y-2">
        <Skeleton className="h-7 w-4/5 md:h-8" />
        <Skeleton className="h-7 w-3/5 md:h-8" />
      </div>
      <Skeleton className="h-4 w-40" />
    </>
  );
}

/** Hero image — mirrors `ProductImages` outer wrapper + main-image box. */
export function OptimisticHero({ seed }: { seed: Seed }) {
  const productName = seed.name || seed.productName || "";
  const link = seed.image_url || seed.image?.link;
  // SFCC-11916: same schedule gate the resolved hero applies (ProductImages), so
  // an expired badge doesn't flash in during the optimistic paint and then
  // disappear when `_product` resolves.
  const badges = getScheduledBadgeTexts(seed);

  return (
    <div className="product-images flex w-full max-w-[575px] flex-col items-start">
      <div className="relative w-full">
        {badges.length > 0 && (
          <div className="absolute left-3 top-3 z-10 flex flex-col gap-1">
            {badges.map((badge, i) => (
              <span
                key={`pdp-badge-${i}`}
                className="bg-accent px-2 py-0.5 text-xs font-medium text-primary-foreground"
              >
                {badge}
              </span>
            ))}
          </div>
        )}
        <div className={cn("mx-0 w-full sm:rounded-xl", PDP_IMAGE_HEIGHT)}>
          {link ? (
            <ProductImage
              image={{ link, alt: productName }}
              // Reuse the grid's cached plp candidate for an instant paint;
              // ProductImages upgrades to the pdp candidate on resolve.
              type="plp"
              lazy={false}
              className={cn("w-full object-contain", PDP_IMAGE_HEIGHT)}
            />
          ) : (
            <Skeleton
              className={cn("w-full sm:rounded-xl", PDP_IMAGE_HEIGHT)}
            />
          )}
        </div>
      </div>

      {/* Thumbnail strip placeholder — most PDPs are multi-image, so the real
          ProductImages renders a strip here; reserving it avoids pushing the
          info column down on mobile (stacked layout) when it appears. Mirrors
          ProductImagesSkeleton's strip. */}
      <div className="flex w-full items-center justify-center gap-2">
        <div className="h-8 w-5 shrink-0" />
        <div className="flex gap-1">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={`thumb-${i}`} className="h-6 w-6 rounded" />
          ))}
        </div>
        <div className="h-8 w-5 shrink-0" />
      </div>
    </div>
  );
}

/**
 * Info column — mirrors the real non-LMN column's full sibling structure
 * (product-details.tsx) section-for-section so the swap to `_product` is
 * CLS-free, not just the top block:
 *
 *   info block (title/brand/SKU/ratings + promo + price) → compare-to →
 *   variant options → divider → cross-sell → divider → qty+ATC
 *
 * Seed-backed where available (title, brand, SKU, ratings, price, promo,
 * compare-to, and the real variant-option buttons from `variationAttributes`);
 * height-reserving placeholders for what the tile can't carry — the short
 * description (skeleton bones), the cross-sell recs (`CrossSellSkeleton`, the
 * same placeholder the real `CrossSellProducts` shows while its CIO recs load),
 * and the qty+ATC row. Returns a fragment (not a wrapper div) so its sections
 * are direct flex children of the column, exactly like the real `<Await>` body.
 */
export function OptimisticInfoColumn({
  seed,
  siteId,
}: {
  seed: Seed;
  siteId: string;
}) {
  const formatPrice = useFormatPrice();
  const name = seed.name || seed.productName || "";
  const brandLink = renderBrandRow(buildBrandRowInput(seed));
  const rating = Number(seed.rating ?? 0);
  const reviewCount = Number(seed.reviewCount ?? 0);
  // Tile semantics: `price` is the current selling price; `c_saleprice` is the
  // compare-at. The PDP main label shows the single current price.
  const price = seed.price ?? seed.c_saleprice;

  return (
    <>
      {/* Info block — title / brand / SKU / ratings / description / promo / price */}
      <div className="relative flex w-full flex-col gap-3 px-5 sm:px-0">
        <div className="space-y-3">
          {/* Title + brand are hoisted above the image on mobile (see the
              resolved column + OptimisticTitleBrand); render them in-column at
              md+ only so the swap doesn't shift them. */}
          <div className="min-[768px]:flex min-[768px]:flex-col min-[768px]:gap-3 hidden">
            <h1 className="font-grotesque text-xl font-medium tracking-tight text-[#142745] md:text-[1.75rem]">
              {name}
            </h1>
            {brandLink}
          </div>
          {seed.id && <p className="text-xs text-gray-500">SKU# {seed.id}</p>}
          <Ratings
            rating={rating}
            reviewCount={reviewCount}
            siteId={siteId}
            size="medium"
          />
          {/* Short description — not carried by the tile: skeleton bones */}
          <div className="space-y-1.5 pt-1">
            <Skeleton className="h-3.5 w-full" />
            <Skeleton className="h-3.5 w-full" />
            <Skeleton className="h-3.5 w-2/3" />
            <Skeleton className="h-3.5 w-20" />
          </div>
        </div>

        <PromotionCallouts
          promotions={seed.productPromotions as HecProduct["productPromotions"]}
        />

        <div className="pt-1">
          {price != null ? (
            <Label className="font-grotesque text-2xl font-medium text-product-price">
              {formatPrice(price)}
            </Label>
          ) : (
            <Skeleton className="h-8 w-28" />
          )}
        </div>
      </div>

      <CompareToMessage
        data={seed.compareToData as HecProduct["compareToData"]}
        siteId={siteId}
      />

      {/* Variant options — real attribute labels + value buttons from the seed,
          mirroring ProductVariantOptions' wrapOptions layout (inert until the
          interactive selector mounts). Renders nothing for non-variant products. */}
      <div className="w-full px-5 sm:px-0">
        <div className="flex flex-col items-stretch gap-4">
          {seed.variationAttributes?.map(attribute => (
            <div
              key={attribute.id}
              className="relative flex w-full flex-col items-start gap-1.5 self-stretch py-0"
            >
              <Label className="w-full text-sm font-semibold">
                Select {attribute.name || attribute.id}:
              </Label>
              <div className="flex flex-row flex-wrap gap-2">
                {attribute.values?.map(v => (
                  <Button
                    key={`${attribute.id}-${v.value}`}
                    type="button"
                    variant="outline"
                    className="inline-block h-10 border-2 border-neutral-3 bg-white px-4 text-neutral-11 shadow-sm"
                  >
                    {v.name}
                  </Button>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      <DividerLine className="mt-4 px-5 sm:px-0" />

      {/* Cross-sell — same placeholder the real CrossSellProducts shows while
          its CIO recs load, so the slot height matches across the swap. */}
      <div className="w-full pl-5 sm:px-0">
        <CrossSellSkeleton />
      </div>

      <DividerLine className="my-4 px-5 sm:px-0" />

      {/* Qty stepper + ATC — interactive; sized to ProductBuyForm's button row.
          Shown at all widths to match the live inline ATC (no longer
          desktop-gated). */}
      <div className="w-full px-5 sm:px-0">
        <div className="mt-5 flex w-full flex-row gap-4 self-stretch">
          <Skeleton className="h-[54px] w-24 rounded-[.5rem]" />
          <Skeleton className="h-[54px] w-full rounded-[.5rem] md:max-w-[175px]" />
        </div>
      </div>
    </>
  );
}

/**
 * Eligibility + free-shipping badges — fully resolvable from the seed
 * (`c_isEligible`) + static site config, so this renders the final markup (no
 * skeleton). Mirrors the badge section in product-details.tsx.
 */
export function OptimisticBadges({
  seed,
  siteId,
}: {
  seed: Seed;
  siteId: string;
}) {
  const isEligible = !!seed.c_isEligible;
  const showsFreeShipping = bySiteId(siteId, SHOWS_FREE_SHIPPING_BADGE);
  const siteLabel = bySiteId(siteId, ELIGIBILITY_SITE_LABEL_BY_SITE) ?? "";

  return (
    <section
      aria-label="product-badges"
      className="min-[768px]:pl-5 min-[768px]:pr-0 mt-3 flex w-full flex-wrap items-center gap-4 pl-5 sm:px-0"
    >
      {/* FSA/HSA only (pairs with free-shipping badge); WDH = SKU pill (SFCC-11640). */}
      {isEligible && siteLabel && showsFreeShipping && (
        <EligibilityBadge siteLabel={siteLabel} />
      )}
      {showsFreeShipping && <FreeShippingBadge />}
    </section>
  );
}
